import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
  Link,
  Button,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from '../../../../../redux/store';
import { useTabs } from '../../../../../context/TabContext';
import { InvoicingPath, RelatedInvoiceType } from '../../../../../utils/globalEnums';
import { fMonthDayYearShort } from '../../../../../utils/formatTime';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import { useDeleteAndNavigate } from '../reusable/hooks/useDeleteAndNavigate';

export const DuplicateInvoicePopup = ({ open, onClose, onSubmitAnyway, submitLabel }) => {
  const { addNewTabAndNavigate } = useTabs();
  const {
    data: {
      duplicateInvoices: { duplicates = [] },
      relatedInvoices: { related = [] },
    },
  } = useSelector((state) => state.invoicing);
  const potentialDuplicates =
    related?.filter(
      (i) => i.type === RelatedInvoiceType.PotentialDuplicate || i.type === RelatedInvoiceType.PotentialSpaceDuplicate
    ) ?? [];

  const hasDuplicates = duplicates?.length > 0;
  const hasPotentialOnly = !hasDuplicates && potentialDuplicates?.length > 0;
  const allPending = duplicates?.every((i) => InvoicingPath[i.path] !== 'previous');
  const deleteAndNavigate = useDeleteAndNavigate();
  const [deleteInvoiceInProgress, setDeleteInvoiceInProgress] = useState(false);

  const handleDeleteInvoice = useCallback(async () => {
    try {
      setDeleteInvoiceInProgress(true);
      await deleteAndNavigate();
    } finally {
      setDeleteInvoiceInProgress(false);
      onClose();
    }
  }, []);
  const invoicesToShow = hasDuplicates ? duplicates : potentialDuplicates;
  const title = hasDuplicates ? 'Duplicate Invoice Found' : 'Potential Duplicate Found';
  const subtitle = hasDuplicates
    ? `Please review and fix before ${
        submitLabel?.startsWith('Submit') || submitLabel?.startsWith('Send') ? 'submitting' : 'saving'
      }`
    : `We found ${potentialDuplicates.length} invoice${
        potentialDuplicates.length > 1 ? 's' : ''
      } with similar details.`;
  if (invoicesToShow.length === 0) return null;

  return (
    <Dialog key={invoicesToShow.map((i) => i.id).join(',')} open={open} onClose={(event, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      onClose();
    }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.7, pt: '18px' }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton size="small" onClick={onClose} disabled={deleteInvoiceInProgress}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" color={'grey'} sx={{ whiteSpace: 'pre-line' }}>
          {subtitle}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle1">Duplicate Invoices:</Typography>
          {invoicesToShow.map((i) => (
            <>
              <Box key={i.id} sx={{ display: 'flex', alignItems: 'center' }}>
                <Link
                  component="button"
                  onClick={() => addNewTabAndNavigate(`/dashboard/invoicing/${InvoicingPath[i.path]}/${i.id}`)}
                  sx={{ fontSize: 14, color: 'primary.main', textDecoration: 'underline' }}
                >
                  {i.vendorInvoiceId} {i.date ? `- ${fMonthDayYearShort(i.date)}` : ''}
                </Link>
                <Typography variant="body2" color={'grey'}>
                  &nbsp;&nbsp;|&nbsp;&nbsp;{InvoicingPath[i.path] === 'previous' ? 'Submitted' : 'Pending'}
                </Typography>
              </Box>
              {hasPotentialOnly && (
                <Typography variant="body2" color="grey" sx={{ fontSize: '11px' }}>
                  {i.type === RelatedInvoiceType.PotentialSpaceDuplicate
                    ? 'Invoice numbers match when spaces are stripped.'
                    : 'Matched by same PO, vendor, facility and total amount.'}
                </Typography>
              )}
            </>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: '18px', gap: 1 }}>
        {(hasPotentialOnly || (hasDuplicates && allPending)) && (
          <Button
            onClick={() =>
              onSubmitAnyway(hasPotentialOnly ? [...new Set(potentialDuplicates?.map((d) => d.type))] : null)
            }
            sx={{ border: '0.8px solid rgba(0,0,0,0.10)', color: 'rgba(28,28,28,0.80)' }}
            disabled={deleteInvoiceInProgress}
          >
            {submitLabel} Anyway
          </Button>
        )}
        {hasPotentialOnly && (
          <AwaitButton
            variant="contained"
            onClick={onClose}
            sx={{ backgroundColor: '#90C34D', '&:hover': { backgroundColor: '#7aab3e' } }}
          >
            Go Back and Review
          </AwaitButton>
        )}
        {hasDuplicates && (
          <AwaitButton
            variant="contained"
            onClick={handleDeleteInvoice}
            sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.main' } }}
            loading={deleteInvoiceInProgress}
            disabled={deleteInvoiceInProgress}
          >
            Delete Current Invoice
          </AwaitButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
DuplicateInvoicePopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmitAnyway: PropTypes.func,
  submitLabel: PropTypes.string,
};
